import { Loader, Modal, useCurrentUser, type PlanInterface } from "@beholos/holoscommon";
import { useCallback, useEffect, useRef, useState } from "react";
import { PaymentModalStep1 } from "./PaymentModalStep1";
import { PaymentModalStep2 } from "./PaymentModalStep2";
import { PaymentModalStep3 } from "./PaymentModalStep3";
import { updatePlan } from "../../services/payment.service";
import type { CardFieldsHandle } from "./CardFields";
// import { getSmartFitPrice, type SmartFitDuration } from "./smartFit.constants";

export type SelectedOptionType = 'LEVEL_0' | 'LEVEL_1' | 'LEVEL_2' | 'LEVEL_3' | 'LEVEL_4' | 'LEVEL_5' | 'LEVEL_6';

export interface PromoCode {
  code: string;
  discountPercent: number;
  firstTimeUsersOnly: boolean;
  excludedPlans?: string[];
}

interface PaymentModalProps {
  isOpen: boolean;
  closeModal: () => void;
  selectedOption: SelectedOptionType;
  selectedPlan: PlanInterface;
  basePlan?: PlanInterface;
  // smartFitInCart: boolean;
  // smartFitDuration: SmartFitDuration;
  // setSmartFitDuration: (duration: SmartFitDuration) => void;
  refetch: () => Promise<void>;
}

const PaymentModalInner = (
  {
    isOpen,
    closeModal,
    selectedOption,
    selectedPlan,
    basePlan,
    // smartFitInCart,
    // smartFitDuration,
    // setSmartFitDuration,
    refetch,
  }: PaymentModalProps
) => {
  const { user } = useCurrentUser();
  // const stripe = useStripe();
  // const elements = useElements();

  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<'today' | 'nextCycle'>('today');
  const [step, setStep] = useState<number>(1);
  const [confirmedTerms, setConfirmedTerms] = useState<{
    terms: boolean,
    price: boolean,
    cancel: boolean,
    autoRenew: boolean,
    smartFitTerms: boolean
  }>({
    terms: false,
    price: false,
    cancel: false,
    autoRenew: false,
    smartFitTerms: false,
  });
  const [saveNewCard, setSaveNewCard] = useState<boolean>(true);
  const [selectedMethod, setSelectedMethod] = useState('new');
  const [appliedPromo, setAppliedPromo] = useState<PromoCode | null>(null);
  const [isSmartFitSelected, /* setIsSmartFitSelected */] = useState(false);
  const [isBrickReady, setIsBrickReady] = useState(false);
  const handleBrickReady = useCallback(() => setIsBrickReady(true), []);
  const cardFieldsRef = useRef<CardFieldsHandle>(null);

  const stepRef = useRef(step)

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    if (isOpen) {
      // setIsSmartFitSelected(smartFitInCart);
      setConfirmedTerms((prev) => ({
        ...prev,
        smartFitTerms: false,
      }));
    }
  }, [isOpen/* , smartFitInCart */]);

  // Called by the Brick's onSubmit after successful tokenization (new card flow)
  const handleBrickSubmit = useCallback(async (formData: any) => {
    try {
      await updatePlan({
        planId: selectedPlan._id,
        paymentMethodId: formData.token,
        appliedPromoCode: appliedPromo ? appliedPromo.code : undefined,
        endOfCycle: selectedPaymentOption === 'nextCycle',
      });

      setLoading(false);
      await refetch();
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al procesar la suscripción al plan');
      setLoading(false);
    }
  }, [selectedPlan._id, appliedPromo, selectedPaymentOption, refetch]);

  const processPayment = async () => {
    if (selectedMethod === 'new') {
      // Trigger the Brick's internal submit button — onSubmit fires with tokenized CardData
      cardFieldsRef.current?.triggerSubmit();
      // Payment completion is handled in handleBrickSubmit via onSubmit callback
    } else {
      // Saved card — process directly
      try {
        await updatePlan({
          planId: selectedPlan._id,
          cardId: selectedMethod,
          appliedPromoCode: appliedPromo ? appliedPromo.code : undefined,
          endOfCycle: selectedPaymentOption === 'nextCycle',
        });

        setLoading(false);
        await refetch();
        setStep(3);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al procesar la suscripción al plan');
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    if (loading) return;

    setLoading(false);
    setStep(1);
    setConfirmedTerms({
      terms: false,
      price: false,
      cancel: false,
      autoRenew: false,
      smartFitTerms: false,
    });
    setSelectedPaymentOption('today');
    setSaveNewCard(true);
    setIsBrickReady(false);
    closeModal();
  };

  const setPaymentDateToday = () => {
    setPaymentDate(new Date());
    setSelectedPaymentOption('today');
  };

  const setPaymentDateNextCycle = () => {
    setPaymentDate(new Date(user?.nextCycle || new Date()));
    setSelectedPaymentOption('nextCycle');
  };

  const goToNextStep = async () => {
    if (loading || isInitializing) return;

    setError(null);
    setIsInitializing(true);

    try {
      if (stepRef.current === 2) {
        setLoading(true);
        await processPayment();
        return;
      }

      setStep(prev => prev + 1);
    } finally {
      setIsInitializing(false);
    }
  };

  const isDisabled = () => {
    if (loading || isInitializing) return true;

    if (step === 1) return false;

    if (step === 2 && selectedMethod === 'new' && !isBrickReady) return true;

    if (step === 2 && selectedPlan) {
      const baseTermsConfirmed = confirmedTerms.terms && confirmedTerms.price && confirmedTerms.autoRenew && confirmedTerms.cancel;
      const smartFitTermsConfirmed = !isSmartFitSelected || confirmedTerms.smartFitTerms;
      return !(baseTermsConfirmed && smartFitTermsConfirmed);
    } else if (step === 2) {
      const baseTermsConfirmed = confirmedTerms.terms && confirmedTerms.price && confirmedTerms.cancel;
      const smartFitTermsConfirmed = !isSmartFitSelected || confirmedTerms.smartFitTerms;
      return !(baseTermsConfirmed && smartFitTermsConfirmed);
    }
  }

  // const smartFitPrice = isSmartFitSelected ? getSmartFitPrice(smartFitDuration) : 0;
  const price = selectedPlan.price - (basePlan?.price || 0) /* + smartFitPrice; */
  const fullPrice = selectedPlan.price;
  const discount = selectedPlan && basePlan ? basePlan.price : 0;
  return (
    <Modal
      containerStyles="bg-white rounded-md shadow-lg max-w-lg w-full mx-4 relative max-h-[90vh] flex flex-col"
      isOpen={isOpen}
      onClose={handleCloseModal}
    >

      {step === 1 && <PaymentModalStep1
        selectedPlan={selectedPlan}
        price={price}
        fullPrice={fullPrice}
        discount={discount}
        paymentDate={paymentDate}
        selectedPaymentOption={selectedPaymentOption}
        setPaymentDateToday={setPaymentDateToday}
        setPaymentDateNextCycle={setPaymentDateNextCycle}
        appliedPromo={appliedPromo}
        setAppliedPromo={setAppliedPromo}
      // isSmartFitSelected={isSmartFitSelected}
      // setIsSmartFitSelected={setIsSmartFitSelected}
      // smartFitDuration={smartFitDuration}
      // setSmartFitDuration={setSmartFitDuration}
      // smartFitPrice={smartFitPrice}
      />}

      {step === 2 && <div className="relative w-full mx-auto flex-1 overflow-y-auto">
        <PaymentModalStep2
          selectedPaymentOption={selectedPaymentOption}
          selectedPlan={selectedPlan}
          basePlan={basePlan}
          confirmedTerms={confirmedTerms}
          setConfirmedTerms={setConfirmedTerms}
          saveNewCard={saveNewCard}
          setSaveNewCard={setSaveNewCard}
          selectedMethod={selectedMethod}
          setSelectedMethod={setSelectedMethod}
          appliedPromo={appliedPromo}
          error={error}
          onBrickReady={handleBrickReady}
          onBrickSubmit={handleBrickSubmit}
          cardFieldsRef={cardFieldsRef}
        // isSmartFitSelected={isSmartFitSelected}
        // smartFitDuration={smartFitDuration}
        // smartFitPrice={smartFitPrice}
        />
        {loading && <div className="flex items-center justify-center absolute top-0 left-0 w-full h-full bg-white/70">
          <Loader />
        </div>}
      </div>}

      {step === 3 && <PaymentModalStep3
        selectedPaymentOption={selectedPaymentOption}
        selectedPlan={selectedPlan}
        selectedOption={selectedOption}
      />}

      {step <= 2 && <div className="p-6 border-t border-gray-200 bg-white rounded-b-md">
        <div className="flex items-center flex-wrap">
          <a
            onClick={handleCloseModal}
            className="btn text-black border border-black px-6 py-1 rounded-full hover:bg-gray-200/30 transition-colors cursor-pointer"
          >
            Cancelar
          </a>
          {(step === 1 || (step === 2 && selectedMethod !== 'new')) && <button
            disabled={isDisabled()}
            className="btn text-white bg-black border border-black px-6 py-1 rounded-full hover:bg-black/80 transition-colors cursor-pointer ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={goToNextStep}
          >
            {step === 1 ? 'Continuar al pago' : 'Finalizar compra'}
          </button>}
        </div>
      </div>}
    </Modal>
  );
}

export const PaymentModal = (props: PaymentModalProps) => {
  return (
    <PaymentModalInner {...props} />
  );
}